import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import Sidebar from './Sidebar';
import { ChevronLeft, Home } from 'lucide-react';

const AppLayout = ({ children, showSidebar = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Pages where sidebar should not be shown
  const noSidebarPaths = ['/', '/login', '/register', '/forgot-password'];
  const isPublicPage = noSidebarPaths.includes(location.pathname) || 
                       location.pathname.startsWith('/schools/');

  if (isPublicPage || !showSidebar) {
    return <>{children}</>;
  }

  // Breadcrumb generation
  const getBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    
    const pathLabels = {
      fr: {
        school: 'École',
        student: 'Élève',
        teacher: 'Enseignant',
        parent: 'Parent',
        settings: 'Paramètres',
        dashboard: 'Tableau de bord',
        attendance: 'Présences',
        timetable: 'Emploi du temps',
        communication: 'Messages',
        fees: 'Frais',
        gradebook: 'Notes',
        homework: 'Devoirs',
        courses: 'Cours',
        quizzes: 'Quiz',
        'report-cards': 'Bulletins',
        analytics: 'Analytiques',
        export: 'Export',
        notifications: 'Notifications',
        ubo: 'Certification UBO',
        'offline-content': 'Contenu hors ligne',
        students: 'Élèves',
        structure: 'Structure',
        calendar: 'Calendrier',
        progress: 'Progression'
      },
      en: {
        school: 'School',
        student: 'Student',
        teacher: 'Teacher',
        parent: 'Parent',
        settings: 'Settings',
        dashboard: 'Dashboard',
        attendance: 'Attendance',
        timetable: 'Timetable',
        communication: 'Messages',
        fees: 'Fees',
        gradebook: 'Grades',
        homework: 'Homework',
        courses: 'Courses',
        quizzes: 'Quizzes',
        'report-cards': 'Report Cards',
        analytics: 'Analytics',
        export: 'Export',
        notifications: 'Notifications',
        ubo: 'UBO Certification',
        'offline-content': 'Offline Content',
        students: 'Students',
        structure: 'Structure',
        calendar: 'Calendar',
        progress: 'Progress'
      }
    };
    
    const labels = pathLabels[language] || pathLabels.fr;
    let currentPath = '';
    
    pathParts.forEach((part, index) => {
      currentPath += `/${part}`;
      const label = labels[part] || part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
      breadcrumbs.push({
        label,
        path: currentPath,
        isLast: index === pathParts.length - 1
      });
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="min-h-screen bg-slate-900 flex">
      <Sidebar />
      
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar with breadcrumbs and back button */}
        <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 px-4 md:px-6 py-3 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Back button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                data-testid="back-button"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">
                  {language === 'fr' ? 'Retour' : 'Back'}
                </span>
              </Button>
              
              {/* Breadcrumbs */}
              <nav className="flex items-center gap-2 text-sm">
                <Link 
                  to="/dashboard" 
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <Home className="h-4 w-4" />
                </Link>
                {breadcrumbs.map((crumb, idx) => (
                  <React.Fragment key={crumb.path}>
                    <span className="text-slate-600">/</span>
                    {crumb.isLast ? (
                      <span className="text-white font-medium">{crumb.label}</span>
                    ) : (
                      <Link 
                        to={crumb.path}
                        className="text-slate-400 hover:text-white transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-slate-400 hover:text-white"
                >
                  <Home className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
